import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../Axios';

const getAxiosConfig = (token) => ({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

const getErrorMessage = (error) =>
  error.response && error.response.data.message ? error.response.data.message : error.message;

const ProjectPlanDayReducer = createSlice({
  name: 'projectPlanDay',
  initialState: {
    projectPlanDayLoading: false,
    projectPlanDaySaving: false,
    projectPlanCalendarLoading: false,
    projectPlanDayFullDetailsLoading: false,
    projectPlanDayTaskLoading: false,
    projectPlanDayList: [],
    projectPlanCalendar: [],
    projectPlanDayFullDetails: null,
    projectPlanDayError: null,
    projectPlanCalendarError: null,
    projectPlanDayFullDetailsError: null,
    projectPlanDayTaskError: null,
    projectPlanDaySuccessMessage: '',
  },
  reducers: {
    saveProjectPlanDayRequest(state) {
      state.projectPlanDaySaving = true;
      state.projectPlanDayError = null;
      state.projectPlanDaySuccessMessage = '';
    },
    saveProjectPlanDaySuccess(state, action) {
      state.projectPlanDaySaving = false;
      state.projectPlanDaySuccessMessage = action.payload || 'Project plan day saved successfully';
    },
    saveProjectPlanDayFail(state, action) {
      state.projectPlanDaySaving = false;
      state.projectPlanDayError = action.payload;
    },
    getProjectPlanDayListRequest(state) {
      state.projectPlanDayLoading = true;
      state.projectPlanDayError = null;
    },
    getProjectPlanDayListSuccess(state, action) {
      state.projectPlanDayLoading = false;
      state.projectPlanDayList = action.payload;
    },
    getProjectPlanDayListFail(state, action) {
      state.projectPlanDayLoading = false;
      state.projectPlanDayError = action.payload;
    },
    getProjectPlanDayByIdRequest(state) {
      state.projectPlanDayLoading = true;
      state.projectPlanDayError = null;
    },
    getProjectPlanDayByIdSuccess(state) {
      state.projectPlanDayLoading = false;
    },
    getProjectPlanDayByIdFail(state, action) {
      state.projectPlanDayLoading = false;
      state.projectPlanDayError = action.payload;
    },
    getProjectPlanCalendarRequest(state) {
      state.projectPlanCalendarLoading = true;
      state.projectPlanCalendarError = null;
    },
    getProjectPlanCalendarSuccess(state, action) {
      state.projectPlanCalendarLoading = false;
      state.projectPlanCalendar = action.payload;
    },
    getProjectPlanCalendarFail(state, action) {
      state.projectPlanCalendarLoading = false;
      state.projectPlanCalendarError = action.payload;
    },
    getProjectPlanDayFullDetailsRequest(state) {
      state.projectPlanDayFullDetailsLoading = true;
      state.projectPlanDayFullDetailsError = null;
    },
    getProjectPlanDayFullDetailsSuccess(state, action) {
      state.projectPlanDayFullDetailsLoading = false;
      state.projectPlanDayFullDetails = action.payload;
    },
    getProjectPlanDayFullDetailsFail(state, action) {
      state.projectPlanDayFullDetailsLoading = false;
      state.projectPlanDayFullDetailsError = action.payload;
    },
    clearProjectPlanDayFullDetails(state) {
      state.projectPlanDayFullDetails = null;
      state.projectPlanDayFullDetailsError = null;
    },
    getProjectPlanDayTaskListRequest(state) {
      state.projectPlanDayTaskLoading = true;
      state.projectPlanDayTaskError = null;
    },
    getProjectPlanDayTaskListSuccess(state) {
      state.projectPlanDayTaskLoading = false;
    },
    getProjectPlanDayTaskListFail(state, action) {
      state.projectPlanDayTaskLoading = false;
      state.projectPlanDayTaskError = action.payload;
    },
  },
});

export const {
  saveProjectPlanDayRequest,
  saveProjectPlanDaySuccess,
  saveProjectPlanDayFail,
  getProjectPlanDayListRequest,
  getProjectPlanDayListSuccess,
  getProjectPlanDayListFail,
  getProjectPlanDayByIdRequest,
  getProjectPlanDayByIdSuccess,
  getProjectPlanDayByIdFail,
  getProjectPlanCalendarRequest,
  getProjectPlanCalendarSuccess,
  getProjectPlanCalendarFail,
  getProjectPlanDayFullDetailsRequest,
  getProjectPlanDayFullDetailsSuccess,
  getProjectPlanDayFullDetailsFail,
  clearProjectPlanDayFullDetails,
  getProjectPlanDayTaskListRequest,
  getProjectPlanDayTaskListSuccess,
  getProjectPlanDayTaskListFail,
} = ProjectPlanDayReducer.actions;

const saveProjectPlanDay = (url, planDayData, token) => async (dispatch) => {
  try {
    dispatch(saveProjectPlanDayRequest());
    const { data } = await axiosInstance.post(url, planDayData, getAxiosConfig(token));

    if (data.status === 1) {
      dispatch(saveProjectPlanDaySuccess(data.message || 'Project plan day saved successfully'));
    } else {
      dispatch(saveProjectPlanDayFail(data.message || 'Unable to save project plan day'));
    }

    return data;
  } catch (error) {
    dispatch(saveProjectPlanDayFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const createProjectPlanDayDispatch = (planDayData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/CreateProjectPlanDay', planDayData, token);

export const updateProjectPlanDayDispatch = (planDayData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/UpdateProjectPlanDay', planDayData, token);

export const createProjectHolidayDispatch = (holidayData, token) =>
  saveProjectPlanDay('/ProjectHoliday/CreateProjectHoliday', holidayData, token);

export const updateProjectHolidayDispatch = (holidayData, token) =>
  saveProjectPlanDay('/ProjectHoliday/UpdateProjectHoliday', holidayData, token);

export const deleteProjectHolidayDispatch = (holidayId, token) => async (dispatch) => {
  try {
    dispatch(saveProjectPlanDayRequest());
    const response = await axiosInstance.delete('/ProjectHoliday/DeleteProjectHoliday', {
      ...getAxiosConfig(token),
      params: {
        planDayId: holidayId,
      },
    });
    const data = response.data || {};
    const success = data.status === 1 || response.status === 204;

    if (success) {
      dispatch(saveProjectPlanDaySuccess(data.message || 'Holiday cancelled successfully'));
    } else {
      dispatch(saveProjectPlanDayFail(data.message || 'Unable to cancel holiday'));
    }

    return success ? { ...data, status: 1 } : data;
  } catch (error) {
    dispatch(saveProjectPlanDayFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const bulkCreateProjectPlanDayDispatch = (planDayData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/BulkCreateProjectPlanDay', planDayData, token);

export const createProjectPlanDayMaterialDispatch = (materialData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/CreateProjectPlanDayMaterial', materialData, token);

export const updateProjectPlanDayMaterialDispatch = (materialData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/UpdateProjectPlanDayMaterial', materialData, token);

export const createProjectPlanDayLabourDispatch = (labourData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/CreateProjectPlanDayLabour', labourData, token);

export const updateProjectPlanDayLabourDispatch = (labourData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/UpdateProjectPlanDayLabour', labourData, token);

export const createProjectPlanDaySubcontractDispatch = (subcontractData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/CreateProjectPlanDaySubcontract', subcontractData, token);

export const updateProjectPlanDaySubcontractDispatch = (subcontractData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/UpdateProjectPlanDaySubcontract', subcontractData, token);

export const createProjectPlanDayTaskDispatch = (taskData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/CreateProjectPlanDayTask', taskData, token);

export const updateProjectPlanDayTaskDispatch = (taskData, token) =>
  saveProjectPlanDay('/ProjectPlanDay/UpdateProjectPlanDayTask', taskData, token);

export const getProjectPlanDayListDispatch = (form, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanDayListRequest());
    const planDto = form?.projectPlanDayDto || {};
    const offset = form?.Offset ?? form?.offset ?? planDto.offset ?? 0;
    const recordCount = form?.RecordCount ?? form?.recordCount ?? planDto.recordCount ?? planDto.pageSize ?? 200;
    const projectId = form?.ProjectId ?? form?.projectId ?? planDto.ProjectId ?? planDto.projectId ?? 0;
    const projectPlanId = form?.ProjectPlanId ?? form?.projectPlanId ?? planDto.ProjectPlanId ?? planDto.projectPlanId ?? 0;

    const { data } = await axiosInstance.get('/ProjectPlanDay/GetProjectPlanDayList', {
      ...getAxiosConfig(token),
      params: {
        Offset: offset,
        RecordCount: recordCount,
        ProjectId: projectId || 0,
        ProjectPlanId: projectPlanId || 0,
      },
    });

    dispatch(getProjectPlanDayListSuccess(data));
    return data;
  } catch (error) {
    dispatch(getProjectPlanDayListFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectPlanDayByIdDispatch = (planDayId, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanDayByIdRequest());
    const { data } = await axiosInstance.get('/ProjectPlanDay/GetProjectPlanDayById', {
      ...getAxiosConfig(token),
      params: {
        PlanDayId: planDayId,
      },
    });

    dispatch(getProjectPlanDayByIdSuccess());
    return data;
  } catch (error) {
    dispatch(getProjectPlanDayByIdFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectPlanCalendarDispatch = (form, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanCalendarRequest());

    const projectId = form?.ProjectId ?? form?.projectId ?? 0;
    const projectPlanId = form?.ProjectPlanId ?? form?.projectPlanId ?? 0;

    const { data } = await axiosInstance.get('/ProjectPlan/GetProjectPlanCalendar', {
      ...getAxiosConfig(token),
      params: {
        ProjectId: projectId || 0,
        ProjectPlanId: projectPlanId || 0,
      },
    });

    dispatch(getProjectPlanCalendarSuccess(data));
    return data;
  } catch (error) {
    dispatch(getProjectPlanCalendarFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectPlanDayFullDetailsDispatch = (planDayId, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanDayFullDetailsRequest());
    const { data } = await axiosInstance.get('/ProjectPlanDay/GetProjectPlanDayFullDetails', {
      ...getAxiosConfig(token),
      params: {
        planDayId,
      },
    });

    dispatch(getProjectPlanDayFullDetailsSuccess(data));
    return data;
  } catch (error) {
    dispatch(getProjectPlanDayFullDetailsFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectPlanDayTaskListDispatch = (planDayId, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanDayTaskListRequest());
    const { data } = await axiosInstance.get('/ProjectPlanDay/GetProjectPlanDayTaskList', {
      ...getAxiosConfig(token),
      params: {
        PlanDayId: planDayId || 0,
      },
    });

    dispatch(getProjectPlanDayTaskListSuccess());
    return data;
  } catch (error) {
    dispatch(getProjectPlanDayTaskListFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const deleteProjectPlanDayTaskDispatch = (planTaskId, token) => async (dispatch) => {
  try {
    dispatch(saveProjectPlanDayRequest());
    const { data } = await axiosInstance.delete('/ProjectPlanDay/DeleteProjectPlanDayTask', {
      ...getAxiosConfig(token),
      params: {
        PlanTaskId: planTaskId,
      },
    });

    if (data.status === 1) {
      dispatch(saveProjectPlanDaySuccess(data.message || 'Task deleted successfully'));
    } else {
      dispatch(saveProjectPlanDayFail(data.message || 'Unable to delete task'));
    }

    return data;
  } catch (error) {
    dispatch(saveProjectPlanDayFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

const getProjectPlanDayResourceList = (url, planDayId, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanDayTaskListRequest());
    const { data } = await axiosInstance.get(url, {
      ...getAxiosConfig(token),
      params: {
        PlanDayId: planDayId || 0,
      },
    });

    dispatch(getProjectPlanDayTaskListSuccess());
    return data;
  } catch (error) {
    dispatch(getProjectPlanDayTaskListFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectPlanDayMaterialListDispatch = (planDayId, token) =>
  getProjectPlanDayResourceList('/ProjectPlanDay/GetProjectPlanDayMaterialList', planDayId, token);

export const getProjectPlanDayLabourListDispatch = (planDayId, token) =>
  getProjectPlanDayResourceList('/ProjectPlanDay/GetProjectPlanDayLabourList', planDayId, token);

export const getProjectPlanDaySubcontractListDispatch = (planDayId, token) =>
  getProjectPlanDayResourceList('/ProjectPlanDay/GetProjectPlanDaySubcontractList', planDayId, token);

const deleteProjectPlanDayResource = (url, paramName, resourceId, token, successMessage) => async (dispatch) => {
  try {
    dispatch(saveProjectPlanDayRequest());
    const { data } = await axiosInstance.delete(url, {
      ...getAxiosConfig(token),
      params: {
        [paramName]: resourceId,
      },
    });

    if (data.status === 1) {
      dispatch(saveProjectPlanDaySuccess(data.message || successMessage));
    } else {
      dispatch(saveProjectPlanDayFail(data.message || 'Unable to delete resource'));
    }

    return data;
  } catch (error) {
    dispatch(saveProjectPlanDayFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const deleteProjectPlanDayMaterialDispatch = (planDayMaterialId, token) =>
  deleteProjectPlanDayResource('/ProjectPlanDay/DeleteProjectPlanDayMaterial', 'PlanDayMaterialId', planDayMaterialId, token, 'Material deleted successfully');

export const deleteProjectPlanDayLabourDispatch = (planDayLabourId, token) =>
  deleteProjectPlanDayResource('/ProjectPlanDay/DeleteProjectPlanDayLabour', 'PlanDayLabourId', planDayLabourId, token, 'Labour deleted successfully');

export const deleteProjectPlanDaySubcontractDispatch = (planDaySubcontractId, token) =>
  deleteProjectPlanDayResource('/ProjectPlanDay/DeleteProjectPlanDaySubcontract', 'PlanDaySubcontractId', planDaySubcontractId, token, 'Contractor deleted successfully');

export default ProjectPlanDayReducer;
